import React, { useState, useMemo, useEffect, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import Label from "../components/form/Label";
import Select from "../components/form/Select";
import InputField from "../components/form/input/InputField";
import TextArea from "../components/form/input/TextArea";
import Button from "../components/ui/button/Button";
import { toast, Toaster } from "react-hot-toast";
import { 
  Layers, UserCheck, Globe, Layout, Loader2, Plus, X, 
  Video, Image as ImageIcon, FileText, PlayCircle, 
  Smartphone, Camera, AlertCircle 
} from "lucide-react";

// API Services
import taskService from "../api/taskService";
import brandService from "../api/brandService";
import postTypeService from "../api/postTypeService";
import userService from "../api/userService"; 

const NO_CAPTION_TEXT = "No Caption";

const getPostTypeIcon = (typeName) => {
  const name = typeName?.toLowerCase() || "";
  if (name.includes("video") || name.includes("reel")) return <Video size={14} />;
  if (name.includes("image") || name.includes("photo") || name.includes("post")) return <ImageIcon size={14} />;
  if (name.includes("story") || name.includes("social")) return <Smartphone size={14} />;
  if (name.includes("carousel")) return <Layers size={14} />;
  if (name.includes("text") || name.includes("blog") || name.includes("caption")) return <FileText size={14} />;
  return <PlayCircle size={14} />; 
};

const STATUS_OPTIONS = ["Pending", "Ready", "Confirmed", "Posted"];

export default function CreateTaskPage() {
  const { isDark } = useOutletContext();
  const navigate = useNavigate();
  
  const [isAddingNewType, setIsAddingNewType] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [addingTypeLoading, setAddingTypeLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [brandOptions, setBrandOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]); 
  const [postTypeOptions, setPostTypeOptions] = useState([]);
  const [existingTasks, setExistingTasks] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const dateRef = useRef(null);
  const isInitialMount = useRef(true);

  const secondaryTextColor = isDark ? 'text-slate-300' : 'text-slate-600';
  const accentBorderColor = isDark ? 'bg-slate-300' : 'bg-slate-600';

  const getTodayDate = () => new Date().toISOString().split('T')[0];

  const [formData, setFormData] = useState({
    brand_id: "",
    post_title: "",
    post_type_id: "",
    task_due_date: getTodayDate(),
    status: "Pending",
    ready_by_id: "",
    confirmed_by_id: "",
    posted_by_id: "",
    description: "",
    link: "" 
  });

  const currentStatusIdx = STATUS_OPTIONS.indexOf(formData.status);

  const currentUser = useMemo(() => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch { return null; }
  }, []);

  const resetForm = () => {
    setFormData({
      brand_id: "",
      post_title: "",
      post_type_id: "",
      task_due_date: getTodayDate(),
      status: "Pending",
      ready_by_id: "",
      confirmed_by_id: "",
      posted_by_id: "",
      description: "",
      link: ""
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (isInitialMount.current) setLoading(true);
      else setLoadingPreview(true);

      try {
        if (isInitialMount.current) {
          const [brandsRes, postTypesRes, usersRes, tasksRes] = await Promise.all([
            brandService.getAllBrands(),
            postTypeService.getActiveTypes(),
            userService.getMinimalUsers(),
            taskService.getTasksByDate(formData.task_due_date)
          ]);

          const brands = (Array.isArray(brandsRes?.data) ? brandsRes.data : brandsRes || [])
            .filter(b => b.is_active === 1);
          const postTypes = Array.isArray(postTypesRes?.data) ? postTypesRes.data : postTypesRes || [];
          const users = (Array.isArray(usersRes?.data) ? usersRes.data : usersRes || [])
            .filter(u => u.is_active === 1);
          const tasks = Array.isArray(tasksRes?.data) ? tasksRes.data : tasksRes || [];

          setBrandOptions(brands.map(b => ({ value: b.id.toString(), label: b.brand_name })));
          const typeOptions = postTypes.map(pt => ({ value: pt.id.toString(), label: pt.type_name }));
          typeOptions.push({ value: "ADD_NEW", label: "+ Add New Post Format" });
          setPostTypeOptions(typeOptions);
          
          const allUsers = users.map(u => ({ value: u.id.toString(), label: u.full_name, role: u.role }));
          setUserOptions(allUsers);
          setManagerOptions(allUsers.filter(u => u.role === 'admin' || u.role === 'manager'));
          setExistingTasks(tasks);
          
          isInitialMount.current = false;
        } else {
          const tasksRes = await taskService.getTasksByDate(formData.task_due_date);
          const tasks = Array.isArray(tasksRes?.data) ? tasksRes.data : tasksRes || [];
          setExistingTasks(tasks);
        }
      } catch (err) {
        toast.error("Error syncing with server");
      } finally {
        setLoading(false);
        setLoadingPreview(false);
      }
    };

    const debounceTimer = setTimeout(fetchData, isInitialMount.current ? 0 : 300);
    return () => clearTimeout(debounceTimer);
  }, [formData.task_due_date]);

  const handleInputChange = (field, value) => {
    let cleanValue = value?.target ? value.target.value : value;
    setFormData((prev) => ({ ...prev, [field]: cleanValue }));
  };

  const handleAddNewType = async () => {
    if (!newTypeName.trim()) { setIsAddingNewType(false); return; }
    setAddingTypeLoading(true);
    try {
      const res = await postTypeService.createType(newTypeName);
      const newId = res.id || res.data?.id;
      const updatedTypes = await postTypeService.getActiveTypes();
      const postTypes = Array.isArray(updatedTypes?.data) ? updatedTypes.data : updatedTypes || [];
      const typeOptions = postTypes.map(pt => ({ value: pt.id.toString(), label: pt.type_name }));
      typeOptions.push({ value: "ADD_NEW", label: "+ Add New Post Format" });
      setPostTypeOptions(typeOptions);

      setFormData(prev => ({ ...prev, post_type_id: newId.toString() }));
      setIsAddingNewType(false);
      setNewTypeName("");
      toast.success("New format added");
    } catch (err) {
      toast.error("Failed to add format");
    } finally {
      setAddingTypeLoading(false);
    }
  };

  const handleSubmit = async () => {
    // 1. Validation check with immediate feedback
    if (!formData.brand_id || !formData.post_title || !formData.task_due_date) {
      toast.error("PLEASE FILL IN ALL REQUIRED FIELDS");
      return;
    }

    setSubmitting(true);
    
    // Create a loading toast and capture its ID so we can replace it later
    const loadingToast = toast.loading("ARCHIVING TASK...");

    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const payload = {
      brand_id: parseInt(formData.brand_id),
      post_type_id: parseInt(formData.post_type_id),
      post_title: formData.post_title,
      task_due_date: formData.task_due_date,
      description: formData.description || null,
      asset_link: formData.link || null,
      status: formData.status,
      claimed_by_id: currentUser?.id ? parseInt(currentUser.id) : null,
      created_at: now,
      ready_by_id: formData.ready_by_id ? parseInt(formData.ready_by_id) : null,
      confirmed_by_id: formData.confirmed_by_id ? parseInt(formData.confirmed_by_id) : null,
      posted_by_id: formData.posted_by_id ? parseInt(formData.posted_by_id) : null,
      readied_at: formData.ready_by_id ? now : null,
      confirmed_at: formData.confirmed_by_id ? now : null,
      posted_at: formData.posted_by_id ? now : null,
      warning_sent: 0
    };

    try {
      await taskService.createTask(payload);
      
      // Replace loading toast with success and set a clear duration to prevent sticking
      toast.success("TASK CREATED SUCCESSFULLY!", { 
        id: loadingToast, 
        duration: 3000 
      });

      resetForm();
      
      // Refresh the preview list on the right side
      const tasksRes = await taskService.getTasksByDate(formData.task_due_date);
      const tasks = Array.isArray(tasksRes?.data) ? tasksRes.data : tasksRes || [];
      setExistingTasks(tasks);

    } catch (err) {
      // Replace loading toast with error
      toast.error("FAILED TO SAVE RECORD", { 
        id: loadingToast, 
        duration: 4000 
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-1 sm:p-6 transition-colors duration-300">
      {/* FIX: Toaster must be outside the 'loading' conditional return 
         so it is present even when the spinner is showing.
      */}
      

      {loading ? (
        <div className="flex-1 flex items-center justify-center min-h-[400px]">
          <Loader2 className="animate-spin text-indigo-500" size={40} />
        </div>
      ) : (
        <>
<div className="relative flex items-center justify-center min-h-[120px] sm:min-h-[100px] -mt-8">
  <div className="relative">
    {/* 1. THE PRECISION LINE: A small orange notch above the header to anchor the eye */}
    
    
    <div className="text-center">
      <h2 className={`text-2xl font-brand-heading font-bold uppercase tracking-tighter ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
        <span className="text-orange-600">CREATE</span> <span className={isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}>Post Task</span>
      </h2>
      {/* <div className="absolute left-1/2 mt-2 -translate-x-1/2 w-20 h-1 bg-orange-500 rounded-full" /> */}
      
      {/* 2. THE SUBHEADER: Spaced out with a subtle border to create "Air" */}
      <div className="flex items-center justify-center gap-3 mt-0">
        <div className={`h-px w-4 ${isDark ? 'bg-slate-400' : 'bg-slate-400'}`} />
        <p className={`text-[13px]  uppercase tracking-[0.1em] ${secondaryTextColor}`}>
          Planning & Scheduling
        </p>
        <div className={`h-px w-4 ${isDark ? 'bg-slate-400' : 'bg-slate-400'}`} />
      </div>
    </div>
  </div>
</div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-start max-w-7xl mx-auto mt-6">
            <div className="lg:col-span-8">
              <div className={`rounded-2xl border p-4 sm:p-8 shadow-sm space-y-8 transition-all ${
                isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'
              }`}>
                
                {/* Section: Brand Identity */}
                <div className="space-y-4">
                  <div className={`flex items-center border-b pb-2 transition-colors ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded-lg ${isDark ? 'bg-slate-500/10 text-[#2D4D94]' : 'bg-slate-50 text-[#2D4D94]'}`}><Globe size={16} /></div>
                      <h3 className={`text-xs font-black uppercase tracking-widest ${secondaryTextColor}`}>Brand</h3>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                    <div className="flex flex-col justify-end text-left">
                      <Label className={`${secondaryTextColor} font-bold mb-2 uppercase text-xs tracking-widest`}>Target Brand <span className="text-orange-600">*</span></Label>
                      <Select options={brandOptions} isDark={isDark} placeholder="Select Partner..." value={formData.brand_id} onChange={(val) => handleInputChange("brand_id", val)} />
                    </div>
                    <div className="flex flex-col justify-end text-left">
                      <Label className={`${secondaryTextColor} font-black mb-2 uppercase text-xs tracking-widest`}>Post Heading <span className="text-orange-600">*</span></Label>
                      <InputField isDark={isDark} placeholder="e.g. New Seasonal Collection" value={formData.post_title} onChange={(e) => handleInputChange("post_title", e.target.value)} />
                    </div>
                  </div>
                </div>

                {/* Section: Specifications */}
                <div className={`space-y-4 pt-4 border-t transition-colors ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-1.5 rounded-lg ${isDark ? 'bg-slate-500/10 text-[#2D4D94]' : 'bg-slate-50 text-[#2D4D94]'}`}><Layers size={16} /></div>
                    <h3 className={`text-xs font-black uppercase tracking-widest ${secondaryTextColor}`}>Content Specifications</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                    <div className="flex flex-col justify-end text-left">
                      <Label className={`${secondaryTextColor} font-black mb-2 uppercase text-xs tracking-widest`}>Post Format <span className="text-orange-600">*</span> </Label>
                      
                      {!isAddingNewType ? (
                        <Select 
                          options={postTypeOptions} 
                          isDark={isDark} 
                          placeholder="Format..." 
                          value={formData.post_type_id} 
                          onChange={(val) => {
                            if (val === "ADD_NEW") setIsAddingNewType(true);
                            else handleInputChange("post_type_id", val);
                          }} 
                        />
                      ) : (
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <InputField 
                              isDark={isDark} 
                              autoFocus
                              placeholder="Type format name..." 
                              value={newTypeName} 
                              onChange={(e) => setNewTypeName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleAddNewType();
                                if (e.key === 'Escape') setIsAddingNewType(false);
                              }}
                            />
                          </div>
                          <div className="flex gap-1">
                            <button 
                              onClick={handleAddNewType}
                              disabled={addingTypeLoading}
                              className={`p-2 rounded-xl border transition-all ${isDark ? 'bg-slate-800 border-slate-700 text-emerald-400' : 'bg-slate-100 border-slate-200 text-emerald-600'}`}
                            >
                              {addingTypeLoading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                            </button>
                            <button 
                              onClick={() => setIsAddingNewType(false)}
                              className={`p-2 rounded-xl border transition-all ${isDark ? 'bg-slate-800 border-slate-700 text-rose-400' : 'bg-slate-100 border-slate-200 text-rose-600'}`}
                            >
                              <X size={18} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col justify-end text-left">
                      <Label className={`${secondaryTextColor} font-black mb-2 uppercase text-xs tracking-widest`}>Due Date <span className="text-orange-600">*</span></Label>
                      <InputField isDark={isDark} ref={dateRef} type="date" value={formData.task_due_date} onChange={(e) => handleInputChange("task_due_date", e.target.value)} />
                    </div>
                  </div>
                </div>

                {/* Section: Workflow */}
                <div className={`space-y-4 pt-4 border-t transition-colors ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-1.5 rounded-lg ${isDark ? 'bg-slate-500/10 text-[#2D4D94]' : 'bg-slate-50 text-[#2D4D94]'}`}><UserCheck size={16} /></div>
                    <h3 className={`text-xs font-black uppercase tracking-widest ${secondaryTextColor}`}>Workflow Progress</h3>
                  </div>
                  
                  <div className={`flex p-1 rounded-2xl gap-1 border shadow-inner transition-all ${
                      isDark ? 'bg-slate-950 border-slate-800' : 'bg-slate-100 border-slate-200'
                  }`}>
                    {STATUS_OPTIONS.map(opt => (
                      <button key={opt} type="button" onClick={() => handleInputChange("status", opt)}
                        className={`flex-1 py-2 text-xs font-black uppercase rounded-xl transition-all ${
                          formData.status === opt 
                          ? (isDark ? "bg-slate-800 text-[#4473d8] shadow-lg border border-slate-700" : "bg-white shadow-sm text-[#2D4D94] border border-slate-200") 
                          : (isDark ? "text-slate-500 hover:text-slate-300" : "text-slate-400 hover:text-slate-600")
                        }`}>{opt}</button>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
                    {currentStatusIdx >= 1 && (
                      <div className="space-y-2">
                        <Label className="!text-orange-500 font-black text-xs uppercase tracking-widest">Ready By</Label>
                        <Select isDark={isDark} options={userOptions} placeholder="Select user..." value={formData.ready_by_id} onChange={(v)=>handleInputChange("ready_by_id", v)} />
                      </div>
                    )}
                    {currentStatusIdx >= 2 && (
                      <div className="space-y-2">
                        <Label className="!text-indigo-500 font-black text-xs uppercase tracking-widest">Confirmed By</Label>
                        <Select isDark={isDark} options={managerOptions} placeholder="Select user..." value={formData.confirmed_by_id} onChange={(v)=>handleInputChange("confirmed_by_id", v)} />
                      </div>
                    )}
                    {currentStatusIdx >= 3 && (
                      <div className="space-y-2">
                        <Label className="!text-emerald-500 font-black text-xs uppercase tracking-widest">Posted By</Label>
                        <Select isDark={isDark} options={userOptions} placeholder="Select user..." value={formData.posted_by_id} onChange={(v)=>handleInputChange("posted_by_id", v)} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Section: Assets */}
                <div className={`space-y-4 pt-4 border-t transition-colors ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <div className="space-y-2 text-left">
                    <Label className={`${secondaryTextColor} font-black text-xs uppercase tracking-widest`}>Asset Link</Label>
                    <InputField isDark={isDark} placeholder="Paste drive link..." value={formData.link} onChange={(e) => handleInputChange("link", e.target.value)} />
                  </div>
                  <div className="space-y-2 text-left">
                    <Label className={`${secondaryTextColor} font-black text-xs uppercase tracking-widest`}>Creative Brief</Label>
                    <TextArea isDark={isDark} rows={3} placeholder="Add requirements..." value={formData.description} onChange={(v) => handleInputChange("description", v)} />
                  </div>
                </div>

<Button 
  isDark={isDark} 
  disabled={submitting} 
  onClick={handleSubmit} 
  className={`
    group relative overflow-hidden flex items-center justify-center gap-2 w-full py-4 rounded-xl font-bold uppercase tracking-wider text-xs transition-all border shadow-sm hover:shadow-lg active:scale-[0.98]
    ${isDark 
      ? 'bg-slate-900/50 border-slate-700 hover:bg-slate-800 hover:border-slate-600' 
      : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300'}
  `}
>
  {/* Glass Shimmer Animation Effect */}
  <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent" />

  {submitting ? (
    <>
      <Loader2 className="animate-spin text-orange-600" size={18} />
      <span className={isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}>Archiving Task...</span>
    </>
  ) : (
    <>
      {/* Orange Accent Icon with subtle bounce/pulse on hover */}
      <Plus 
        size={18} 
        strokeWidth={3} 
        className="text-orange-600 transition-transform duration-500 ease-out group-hover:rotate-90 group-hover:scale-110" 
      />
      <span className={isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}>
        Save Content Task
      </span>
    </>
  )}
</Button>
              </div>
            </div>

            {/* PREVIEW PANEL */}
            <div className="lg:col-span-4 lg:sticky lg:top-6">
              <div className={`rounded-2xl border p-6 shadow-sm space-y-4 transition-all ${
                  isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'
              }`}>
                <h3 className={`text-xs font-black uppercase tracking-widest transition-colors ${secondaryTextColor}`}>
                  Schedule Preview: {formData.task_due_date}
                </h3>
                
                <div className={`rounded-2xl border p-4 min-h-[400px] space-y-3 transition-all ${
                    isDark ? 'bg-slate-950 border-slate-800' : 'bg-slate-50 border-slate-100'
                }`}>
                  {loadingPreview ? (
                    <div className="h-full flex flex-col items-center justify-center opacity-30 py-10">
                       <Loader2 className="animate-spin mb-2" size={24} />
                    </div>
                  ) : existingTasks.length > 0 ? (
                    <div className="space-y-2">
                      {existingTasks.map((t, idx) => (
                        <div key={idx} className={`group relative p-2.5 pl-4 rounded-xl border transition-all hover:shadow-md ${isDark ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200 shadow-sm'}`}>
                          <div className={`absolute left-0 top-2 bottom-2 w-1 rounded-r-full ${isDark ? 'bg-[#5A9AFF]' : 'bg-[#2D4D94]'}`} />
                          
                          <div className="flex flex-col text-left">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                                {getPostTypeIcon(t.type_name)}
                              </span>
                              <p className={`text-[10px] font-black uppercase tracking-widest ${secondaryTextColor}`}>
                                {t.type_name || 'Task'}
                              </p>
                            </div>
                            <p className={`text-xs font-black uppercase truncate leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {t.brand_name}
                            </p>
                            <p className={`text-[10px] font-bold truncate opacity-80 ${secondaryTextColor}`}>
                              {t.post_title?.trim() || NO_CAPTION_TEXT}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-center py-10 opacity-30">
                      <Layout size={32} className="mx-auto mb-2 text-slate-400" />
                      <p className={`text-xs font-black uppercase tracking-widest ${secondaryTextColor}`}>No other tasks</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
